import React from 'react';
import { motion } from 'framer-motion';
import { Lightbulb, Thermometer, Lock, Zap, Smartphone } from 'lucide-react';
import { Button } from './ui/button';
import { useTranslation } from 'react-i18next';

export const HeroModern = () => {
  const { t } = useTranslation();
  
  // Animation variants
  const fadeUpVariant = {
    hidden: { opacity: 0, y: 30 },
    visible: (custom) => ({
      opacity: 1,
      y: 0,
      transition: { delay: custom * 0.2, duration: 0.8, ease: 'easeOut' }
    })
  };

  const floatVariant = (duration) => ({
    animate: {
      y: [-10, 10, -10],
      transition: {
        duration,
        repeat: Infinity,
        ease: 'easeInOut'
      }
    }
  });

  return (
    <section 
      id="home" 
      className="relative min-h-screen bg-[#050505] overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Image with Gradient Overlay */}
      <div className="absolute inset-0 z-0">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1724582586495-d050726cf354?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBtb2Rlcm4lMjBsaXZpbmclMjByb29tJTIwZGFyayUyMG1vb2R5fGVufDB8fHx8MTc4NDA1MzcyMnww&ixlib=rb-4.1.0&q=85')`
          }}
        />
        {/* Gradient overlay - darker on left, transparent on right */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/90 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24 min-h-screen flex items-center">
        <div className="grid lg:grid-cols-2 gap-12 items-center w-full">
          {/* Left Side - Text Content */}
          <div className="space-y-8">
            {/* Overline */}
            <motion.div
              initial="hidden"
              animate="visible"
              custom={0}
              variants={fadeUpVariant}
            >
              <span 
                className="inline-block px-4 py-2 rounded-full bg-[#C5A35C]/10 border border-[#C5A35C]/30 text-[#C5A35C] text-xs font-mono uppercase tracking-[0.2em]"
                data-testid="hero-overline"
              >
                {t('hero.badge')}
              </span>
            </motion.div>

            {/* Main Headline */}
            <motion.h1
              initial="hidden"
              animate="visible"
              custom={1}
              variants={fadeUpVariant}
              className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white tracking-tighter leading-none"
              style={{ fontFamily: "'Outfit', sans-serif" }}
              data-testid="hero-headline"
            >
              {t('hero.title')}{' '}
              <span className="text-[#C5A35C]">{t('hero.title.highlight')}</span>
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              initial="hidden"
              animate="visible"
              custom={2}
              variants={fadeUpVariant}
              className="text-lg sm:text-xl text-gray-300 leading-relaxed max-w-xl"
              style={{ fontFamily: "'Manrope', sans-serif" }}
              data-testid="hero-description"
            >
              {t('hero.description')}
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial="hidden"
              animate="visible"
              custom={3}
              variants={fadeUpVariant}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Button
                size="lg"
                className="bg-[#C5A35C] hover:bg-[#E0C382] text-white px-8 py-6 text-lg rounded-full font-medium transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(197,163,92,0.3)]"
                onClick={() => window.open(`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}`, '_blank')}
                data-testid="hero-cta-whatsapp"
              >
                <Smartphone className="mr-2 h-5 w-5" />
                {t('hero.cta.whatsapp')}
              </Button>
              
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white/20 text-white hover:bg-white/5 hover:border-white/40 px-8 py-6 text-lg rounded-full font-medium backdrop-blur-sm transition-all duration-300"
                onClick={() => document.querySelector('#solutions')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-cta-demo"
              >
                {t('hero.cta.demo')}
              </Button>
            </motion.div>

            {/* Trust Indicators */}
            <motion.div
              initial="hidden"
              animate="visible"
              custom={4}
              variants={fadeUpVariant}
              className="flex items-center gap-6 pt-4"
            >
              <div className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-[#C5A35C]" />
                <span className="text-sm text-gray-400">{t('hero.security')}</span>
              </div>
            </motion.div>
          </div>

          {/* Right Side - Floating Domotics Widgets */}
          <div className="relative hidden lg:block h-[600px]">
            {/* Lighting Control Widget */}
            <motion.div
              variants={floatVariant(4)}
              animate="animate"
              className="absolute top-20 right-20 backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-5 shadow-2xl w-72"
              data-testid="widget-lighting"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-[#C5A35C]/20 flex items-center justify-center">
                    <Lightbulb className="w-6 h-6 text-[#C5A35C] drop-shadow-[0_0_15px_rgba(197,163,92,0.6)]" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-sm">Iluminación</h4>
                    <p className="text-gray-400 text-xs">Sala Principal</p>
                  </div>
                </div>
                <div className="text-[#C5A35C] font-bold text-xl">70%</div>
              </div>
              
              {/* Slider Visual */}
              <div className="space-y-2">
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full w-[70%] bg-gradient-to-r from-[#C5A35C] to-[#E0C382] rounded-full shadow-[0_0_10px_rgba(197,163,92,0.5)]" />
                </div>
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Apagado</span>
                  <span>Máximo</span>
                </div>
              </div>
            </motion.div>

            {/* Climate Control Widget */}
            <motion.div
              variants={floatVariant(5)}
              animate="animate"
              className="absolute bottom-40 right-32 backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-5 shadow-2xl w-64"
              data-testid="widget-climate"
            >
              <div className="text-center mb-4">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#C5A35C]/20 mb-3">
                  <Thermometer className="w-8 h-8 text-[#C5A35C] drop-shadow-[0_0_15px_rgba(197,163,92,0.6)]" />
                </div>
                <h4 className="text-white font-semibold mb-1">Clima</h4>
                <div className="text-4xl font-bold text-[#C5A35C]">22°C</div>
                <p className="text-gray-400 text-sm mt-1">✨ Temperatura Ideal</p>
              </div>
              
              <div className="flex gap-2">
                <button className="flex-1 py-2 px-3 rounded-lg bg-white/5 hover:bg-white/10 text-white text-xs font-medium transition-colors">
                  -1°
                </button>
                <button className="flex-1 py-2 px-3 rounded-lg bg-white/5 hover:bg-white/10 text-white text-xs font-medium transition-colors">
                  +1°
                </button>
              </div>
            </motion.div>

            {/* Security Widget */}
            <motion.div
              variants={floatVariant(3.5)}
              animate="animate"
              className="absolute top-60 right-8 backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-4 shadow-2xl w-56"
              data-testid="widget-security"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Lock className="w-5 h-5 text-green-400 drop-shadow-[0_0_10px_rgba(34,197,94,0.6)]" />
                </div>
                <div className="flex-1">
                  <h4 className="text-white font-semibold text-sm">Seguridad</h4>
                  <p className="text-green-400 text-xs font-medium">🟢 Sistema Activo</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2"
        >
          <div className="w-1.5 h-3 bg-[#C5A35C] rounded-full" />
        </motion.div>
      </motion.div>
    </section>
  );
};
